<template>
  <div>
    <!-- Loading state -->
    <div
      v-if="contactStore.loading"
      class="flex justify-center items-center py-12"
    >
      <ProgressSpinner />
    </div>

    <!-- Error state -->
    <div
      v-else-if="contactStore.error"
      class="bg-red-50 border border-red-200 rounded-lg p-4 mb-4"
    >
      <div class="flex items-center">
        <i class="pi pi-exclamation-triangle text-red-500 mr-2" />
        <span class="text-red-700">Error loading contacts: {{ contactStore.error }}</span>
      </div>
    </div>

    <!-- Main content -->
    <div v-else>
      <!-- Search Bar -->
      <div class="bg-white rounded-lg shadow-sm p-4  w-full mb-4">
        <div class="flex flex-wrap items-center justify-between gap-2 p-4">
          <div class="w-64">
            <SearchBarComponent
              :data="contacts"
              search-key="name"
              @filtered="handleSearchFilter"
            />
          </div>
          <div class="ml-auto">
            <Button
              icon="pi pi-plus"
              label="Add Contact"
              class="p-button-sm"
              severity="secondary"
              raised
              @click="openAddModal"
            />
          </div>
        </div>
      </div>

      <!-- Alphabet Filter -->
      <div class="bg-white rounded-lg shadow-sm p-4 w-full mb-4">
        <AlphabetFilterComponent 
          :data="contacts" 
          filter-key="name" 
          @filtered="handleLetterFilter"
        />
      </div>

      <!-- Contacts Card Layout -->
      <div class="bg-white rounded-lg shadow-sm p-4 w-full">
        <!-- Empty state message when no contacts match filters -->
        <div
          v-if="filteredContacts.length === 0"
          class="text-center py-8"
        >
          <i class="pi pi-users text-5xl text-gray-300 mb-3" />
          <p class="text-gray-500">
            No contacts match your criteria
          </p>
        </div>

        <!-- Card Layout -->
        <div
          v-else
          class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
        >
          <div
            v-for="contact in filteredContacts"
            :key="contact.id"
            class="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden cursor-pointer"
            @click="viewContact(contact.id)"
          >
            <!-- Contact Header with Avatar -->
            <div
              class="h-48 relative flex items-start justify-center pt-8"
              :style="{ backgroundColor: getRandomColor(contact.id) }"
            >
              <!-- Profile image or placeholder -->
              <div
                class="w-24 h-24 rounded-full overflow-hidden border-4 border-white flex items-center justify-center"
              >
                <img
                  v-if="contact.image || contact.image_url"
                  :src="contact.image_url || contact.image"
                  :alt="contact.name"
                  class="w-full h-full object-cover"
                >
                <div
                  v-else
                  class="w-full h-full flex items-center justify-center bg-gray-400"
                >
                  <i class="pi pi-user text-white text-4xl" />
                </div>
              </div>

              <!-- Company label if available -->
              <div
                v-if="contact.company_name"
                class="absolute bottom-2 left-2 bg-white bg-opacity-90 px-3 py-1 rounded-lg text-sm font-medium shadow-sm"
              >
                {{ contact.company_name }}
              </div>
            </div>

            <!-- Contact Details -->
            <div class="p-4">
              <h3 class="text-lg font-semibold text-gray-800 mb-2">
                {{ contact.name }}
              </h3>

              <div class="flex items-center text-sm text-gray-600 mb-1">
                <i class="pi pi-envelope mr-2" />
                <span class="truncate">{{ contact.email }}</span>
              </div>

              <div class="flex items-center text-sm text-gray-600 mb-1">
                <i class="pi pi-phone mr-2" />
                <span>{{ contact.phone }}</span>
              </div>

              <!-- Position if available -->
              <div
                v-if="contact.position"
                class="flex items-center text-sm text-gray-600 mb-1"
              >
                <i class="pi pi-briefcase mr-2" />
                <span>{{ contact.position }}</span>
              </div>

              <!-- Tags or categories if available -->
              <div
                v-if="contact.tags && contact.tags.length > 0"
                class="flex flex-wrap gap-1 mt-2"
              >
                <span
                  v-for="(tag, index) in contact.tags.slice(0, 3)"
                  :key="index"
                  class="px-2 py-1 bg-blue-100 text-blue-800 rounded-md text-xs"
                >
                  {{ tag }}
                </span>
              </div>
            </div>

            <!-- Action Footer -->
            <div class="px-4 py-2 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
              <div>
                <!-- Status indicator if applicable -->
                <span
                  v-if="contact.is_bookable"
                  class="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full"
                >
                  Bookable
                </span>
              </div>
              <Button
                icon="pi pi-eye"
                class="p-button-rounded p-button-text p-button-sm"
                @click.stop="viewContact(contact.id)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Dialog for Add Contact -->
    <Dialog
      v-model:visible="isDialogVisible"
      :header="dialogHeader"
      :style="{ width: '90vw', maxWidth: '1200px' }"
      :modal="true"
      :closable="true"
    >
      <ContactModal
        :data="selectedContact"
        :type="modalType"
        @contact-created="handleContactCreated"
      />
    </Dialog>
  </div>
</template>

<script setup>
//Pinia Stores
import { useContactStore } from '@/stores/contactsStore';
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import ContactModal from '@/components/contacts/modals/ContactModal.vue';
import SearchBarComponent from '@/components/shared/tableSearch/TableSearch.vue';
import AlphabetFilterComponent from '@/components/shared/alphabetFilter/AlphabetFilterComponent.vue';

// Initialize router for navigation
const router = useRouter();

// Initialize Pinia store and load contacts if not already loaded
const contactStore = useContactStore();
const contacts = computed(() => contactStore.contacts); // Use computed for reactivity

// Dialog state
const isDialogVisible = ref(false);
const dialogHeader = ref('Contact Details');
const modalType = ref('add');
const selectedContact = ref({});

// Filtering state
const searchFilteredContacts = ref([]);
const letterFilteredContacts = ref([]);
const filteredContacts = ref([]);

// Apply combined filters
const applyFilters = () => {
    if (letterFilteredContacts.value.length === contacts.value.length) {
        // If letter filter is not active, just use search results
        filteredContacts.value = searchFilteredContacts.value;
    } else if (searchFilteredContacts.value.length === contacts.value.length) {
        // If search is not active, just use letter filter
        filteredContacts.value = letterFilteredContacts.value;
    } else {
        // Both filters active - find intersection
        filteredContacts.value = searchFilteredContacts.value.filter(contact =>
            letterFilteredContacts.value.some(c => c.id === contact.id)
        );
    }
};

// Handle filtered results from search
const handleSearchFilter = (filtered) => {
    searchFilteredContacts.value = filtered;
    applyFilters();
};

// Handle filtered results from alphabet (client-side)
const handleLetterFilter = (filtered) => {
    letterFilteredContacts.value = filtered;
    applyFilters();
};

// Watch for changes in contacts data
watch(contacts, (newContacts) => {
    // Ensure newContacts is an array before spreading
    const contactsArray = Array.isArray(newContacts) ? newContacts : [];
    searchFilteredContacts.value = [...contactsArray];
    letterFilteredContacts.value = [...contactsArray];
    applyFilters();
}, { immediate: true });

// Initialize filters when component mounts
onMounted(async () => {
    // Load contacts from API
    await contactStore.loadContacts();
    
    // Initialize filtered contacts - ensure contacts is an array
    const contactsArray = Array.isArray(contacts.value) ? contacts.value : [];
    searchFilteredContacts.value = [...contactsArray];
    letterFilteredContacts.value = [...contactsArray];
    applyFilters();
});

// Navigate to view a specific contact
const viewContact = (id) => {
    router.push(`/view-contact/${id}/overview`);
};

// Open dialog to add a new contact
const openAddModal = () => {
    selectedContact.value = {};
    modalType.value = 'add';
    dialogHeader.value = 'Add Contact';
    isDialogVisible.value = true;
};

// Handle contact created event
const handleContactCreated = () => {
    isDialogVisible.value = false;
    // Reload contacts list
    contactStore.loadContacts();
};

// Function to generate a random color based on contact ID
const getRandomColor = (id) => {
    const colors = [
        '#FF6B6B', // Coral Red
        '#4ECDC4', // Turquoise
        '#45B7D1', // Sky Blue
        '#96CEB4', // Sage Green
        '#FFEEAD', // Light Yellow
        '#D4A5A5', // Dusty Rose
        '#9B59B6', // Purple
        '#3498DB', // Blue
        '#E67E22', // Orange
        '#2ECC71', // Green
        '#F1C40F', // Yellow
        '#E74C3C', // Red
        '#1ABC9C', // Teal
        '#34495E', // Dark Blue
        '#F39C12', // Gold
    ];
    // Use the contact ID to get a consistent color for each contact
    return colors[id % colors.length];
};
</script>

<style scoped>
.truncate {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 200px;
}
</style>
